/**
 * Rotating Text State Manager - Simple Vertical List
 * 
 * Dead simple: vertical list of words, translate container up.
 */

export interface RotatingTextState {
  currentOffset: number; // Vertical offset in pixels (translateY value)
  targetWordIndex: number; // The word we're rolling towards
  isRolling: boolean; // Whether currently rolling
  rollStartTime: number; // When current roll started
  nextCycleTime: number; // When next cycle should start (1 hour after settling)
  phase: 'initial-roll' | 'settled' | 'hourly-roll'; // Current animation phase
}

const WORDS = [
  'Decision',
  'Inference',
  'Clarity',
  'Navigation',
  'Strategy',
  'Yes No',
  'Life',
  'Homeostasis',
  'Intelligence',
  'Decision',
];

// Find "Decision" index (capitalized)
const DECISION_INDEX = WORDS.indexOf('Decision');

const CONFIG = {
  wordHeight: 10, // Height of each word in pixels (matches taglineFontSize / 2 for DPR)
  initialRollSpeed: 0.15, // Speed of initial roll (words per second)
  hourlyRollSpeed: 0.08, // Speed of hourly roll (slower, more deliberate)
  settleDuration: 800, // Time to settle on final word (ms)
  cycleInterval: 3600000, // 1 hour in milliseconds
  initialRollDelay: 500, // Wait 500ms after page load before starting initial roll
};

let rotatingTextState: RotatingTextState = {
  currentOffset: 0,
  targetWordIndex: DECISION_INDEX,
  isRolling: false,
  rollStartTime: 0,
  nextCycleTime: 0,
  phase: 'initial-roll',
};

let pageLoadTime: number = 0;

/**
 * Ease-out cubic function for smooth deceleration
 */
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Initialize rotating text state
 */
export function initializeRotatingText(): void {
  pageLoadTime = performance.now();
  rotatingTextState = {
    currentOffset: 0,
    targetWordIndex: DECISION_INDEX,
    isRolling: false,
    rollStartTime: pageLoadTime + CONFIG.initialRollDelay,
    nextCycleTime: 0,
    phase: 'initial-roll',
  };
}

/**
 * Update rotating text state
 * Should be called every frame in the animation loop
 */
export function updateRotatingText(currentTime: number): void {
  const timeSincePageLoad = currentTime - pageLoadTime;
  
  // Handle initial roll phase
  if (rotatingTextState.phase === 'initial-roll') {
    if (timeSincePageLoad < CONFIG.initialRollDelay) {
      rotatingTextState.currentOffset = 0;
      return;
    }
    
    if (!rotatingTextState.isRolling) {
      rotatingTextState.isRolling = true;
      rotatingTextState.rollStartTime = currentTime;
      rotatingTextState.targetWordIndex = 0; // Start from first word
    }
    
    // Calculate how many words to roll through (all words, ending on decision)
    const wordsToRoll = DECISION_INDEX === 0 ? WORDS.length : DECISION_INDEX + WORDS.length;
    const rollDuration = wordsToRoll / CONFIG.initialRollSpeed * 1000;
    const elapsed = currentTime - rotatingTextState.rollStartTime;
    
    if (elapsed < rollDuration) {
      // Still rolling - calculate offset
      const progress = elapsed / rollDuration;
      const wordProgress = (progress * wordsToRoll) % 1;
      const currentWordIndex = Math.floor(progress * wordsToRoll) % WORDS.length;
      
      // Update target word index
      rotatingTextState.targetWordIndex = (currentWordIndex + 1) % WORDS.length;
      
      // Calculate offset: negative means moving up
      rotatingTextState.currentOffset = -wordProgress * CONFIG.wordHeight;
    } else {
      // Rolling complete, start settling
      const settleElapsed = elapsed - rollDuration;
      const finalOffset = -DECISION_INDEX * CONFIG.wordHeight;
      
      if (settleElapsed < CONFIG.settleDuration) {
        // Ease from current position to final position
        const settleProgress = easeOutCubic(settleElapsed / CONFIG.settleDuration);
        const startOffset = -(wordsToRoll % WORDS.length) * CONFIG.wordHeight;
        rotatingTextState.currentOffset = startOffset + (finalOffset - startOffset) * settleProgress;
        rotatingTextState.targetWordIndex = DECISION_INDEX;
      } else {
        // Settled on decision
        rotatingTextState.currentOffset = finalOffset;
        rotatingTextState.targetWordIndex = DECISION_INDEX;
        rotatingTextState.isRolling = false;
        rotatingTextState.phase = 'settled';
        rotatingTextState.nextCycleTime = currentTime + CONFIG.cycleInterval;
      }
    }
    return;
  }
  
  // Handle settled phase - wait for next cycle
  if (rotatingTextState.phase === 'settled') {
    if (currentTime >= rotatingTextState.nextCycleTime) {
      // Time for hourly cycle
      rotatingTextState.phase = 'hourly-roll';
      rotatingTextState.isRolling = true;
      rotatingTextState.rollStartTime = currentTime;
      // Cycle to next word
      rotatingTextState.targetWordIndex = (rotatingTextState.targetWordIndex + 1) % WORDS.length;
    } else {
      // Still settled - maintain offset for current word
      rotatingTextState.currentOffset = -rotatingTextState.targetWordIndex * CONFIG.wordHeight;
    }
    return;
  }
  
  // Handle hourly roll phase
  if (rotatingTextState.phase === 'hourly-roll') {
    if (!rotatingTextState.isRolling) {
      return;
    }
    
    const elapsed = currentTime - rotatingTextState.rollStartTime;
    const rollDuration = 1 / CONFIG.hourlyRollSpeed * 1000;
    
    if (elapsed < rollDuration) {
      // Still rolling - translate up one word height
      const progress = elapsed / rollDuration;
      const startOffset = -(rotatingTextState.targetWordIndex === 0 ? WORDS.length - 1 : rotatingTextState.targetWordIndex - 1) * CONFIG.wordHeight;
      const endOffset = -rotatingTextState.targetWordIndex * CONFIG.wordHeight;
      rotatingTextState.currentOffset = startOffset + (endOffset - startOffset) * progress;
    } else {
      // Rolling complete, start settling
      const settleElapsed = elapsed - rollDuration;
      if (settleElapsed < CONFIG.settleDuration) {
        const settleProgress = easeOutCubic(settleElapsed / CONFIG.settleDuration);
        const startOffset = -(rotatingTextState.targetWordIndex === 0 ? WORDS.length - 1 : rotatingTextState.targetWordIndex - 1) * CONFIG.wordHeight;
        const endOffset = -rotatingTextState.targetWordIndex * CONFIG.wordHeight;
        rotatingTextState.currentOffset = startOffset + (endOffset - startOffset) * settleProgress;
      } else {
        // Settled on target word
        rotatingTextState.currentOffset = -rotatingTextState.targetWordIndex * CONFIG.wordHeight;
        rotatingTextState.isRolling = false;
        rotatingTextState.phase = 'settled';
        rotatingTextState.nextCycleTime = currentTime + CONFIG.cycleInterval;
      }
    }
  }
}

/**
 * Get all words for rendering
 */
export function getAllWords(): string[] {
  return WORDS;
}

/**
 * Get current offset for container translation
 */
export function getCurrentOffset(): number {
  return rotatingTextState.currentOffset;
}

/**
 * Get current word to display (for simple text replacement)
 */
export function getCurrentWord(): string {
  return WORDS[rotatingTextState.targetWordIndex];
}

/**
 * Get rotating text state (for debugging)
 */
export function getRotatingTextState(): Readonly<RotatingTextState> {
  return { ...rotatingTextState };
}
